import React, { useState } from 'react';
import { communityServicesAPI } from '../../../services/api';
import NewCategoryModal from './NewCategoryModal';
import { useQuery } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import ConfirmationModal from '../../common/ConfirmationModal';
import EditCategoryModal from './EditCategoryModal';
import toast from 'react-hot-toast';
import { EyeIcon, PencilIcon, TrashIcon, PlusIcon, FolderIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const ManageCategoryModal = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['programCategories'],
    queryFn: async () => {
      const res = await communityServicesAPI.getCategories({ per_page: 1000 });
      const categories = res.data?.data?.items ?? [];
      const sortedCategories = [...categories].sort((a, b) => {
        const dateA = a.created_at || a.created_date || a.createdAt || a.date_created || 0;
        const dateB = b.created_at || b.created_date || b.createdAt || b.date_created || 0;
        return new Date(dateB) - new Date(dateA);
      });
      return { items: sortedCategories, meta: res.data?.data?.meta ?? {} };
    },
    enabled: !!isOpen,
    refetchOnWindowFocus: true,
    refetchInterval: isOpen ? 30000 : false,
    staleTime: 15000,
    cacheTime: 30000,
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (categoryId) => communityServicesAPI.deleteProgramCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programCategories'] });
      refetch();
      setDeletingCategoryId(null);
      toast.success('Category deleted successfully!');
    },
    onError: (err) => {
      console.error('Failed to delete category', err);
      toast.error(err?.response?.data?.message || 'Failed to delete category');
    }
  });

  const items = Array.isArray(data?.items) ? data.items : [];

  const filteredData = items
    .filter((category) => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        String(category.name || '').toLowerCase().includes(q) ||
        String(category.description || '').toLowerCase().includes(q)
      );
    })
    .filter((category) => (statusFilter === 'all' ? true : category.status === statusFilter));

  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const startIndex = totalItems ? (currentPage - 1) * rowsPerPage : 0;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const currentData = filteredData.slice(startIndex, endIndex);

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (categoryId) => {
    const cat = currentData.find(c => c.category_id === categoryId);
    setSelectedCategory(cat || null);
    setShowEditModal(true);
  };

  const handleDelete = (categoryId) => {
    setDeletingCategoryId(categoryId);
    setIsConfirmOpen(true);
  };

  const handleNewCategory = () => {
    setShowNewCategoryModal(true);
  };

  const handleCategoryAdded = (newCategory) => {
    console.log('New category added:', newCategory);
    queryClient.invalidateQueries({ queryKey: ['programCategories'] });
    refetch();
    setCurrentPage(1);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedCategory(null);
    queryClient.invalidateQueries({ queryKey: ['programCategories'] });
  };

  React.useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm('');
      refetch();
    }
  }, [isOpen, refetch]);

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-6xl mx-auto max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-900">Manage Categories</h3>
              {isLoading && (
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  Refreshing...
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                {/* New Category Button */}
                <button
                  onClick={handleNewCategory}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  disabled={isLoading}
                >
                  <PlusIcon className="w-4 h-4" />
                  New Category
                </button>
                {/* Status filter */}
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    className="appearance-none border border-gray-300 rounded-md pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                  >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
                  </svg>
                </div>


              </div>

              {/* Search bar */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search category..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="border border-gray-300 rounded-md pl-10 pr-3 py-2 text-sm w-96 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Table Container with Border */}
          <div className="flex-1 overflow-auto p-6">
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              {/* Loading State */}
              {isLoading && (
                <div className="flex justify-center items-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <div className="text-gray-600">Loading categories...</div>
                  </div>
                </div>
              )}

              {/* Error State */}
              {isError && !isLoading && (
                <div className="flex justify-center items-center py-8">
                  <div className="text-red-600">{error?.message || 'Failed to load categories'}</div>
                </div>
              )}

              {/* Table */}
              {!isLoading && !isError && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name of Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentData.length > 0 ? (
                        currentData.map((category) => (
                          <tr key={category.category_id} className="hover:bg-gray-50">
                            {/* Name of Category */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div
                                  className="flex items-center justify-center w-10 h-10 rounded-lg"
                                  style={{ backgroundColor: '#E0E7FF' }}
                                >
                                  <FolderIcon className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900">
                                    {category.name}
                                  </span>
                                  {/* New badge*/}
                                  {category.isNew && (
                                    <span className="text-xs text-blue-600 font-medium mt-1">
                                      New
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Description */}
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-md">
                                {category.description}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${category.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                                  }`}
                                style={{
                                  backgroundColor: category.status === 'active' ? '#DCFCE7' : '#FEE2E2',
                                  color: category.status === 'active' ? '#15803D' : '#DC2626'
                                }}
                              >
                                {category.status.charAt(0).toUpperCase() + category.status.slice(1)}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-end space-x-1">
                                {/* Edit button */}
                                <button
                                  onClick={() => handleEdit(category.category_id)}
                                  className="text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110"
                                  title="Edit Category"
                                  disabled={deleteCategoryMutation.isPending}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>

                                {/* Delete button */}
                                <button
                                  onClick={() => handleDelete(category.category_id)}
                                  className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50 transition-all duration-200 hover:scale-110"
                                  title="Delete Category"
                                  disabled={deleteCategoryMutation.isPending}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                            {totalItems === 0 ? 'No categories found.' : 'No categories found matching your search.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          {!isLoading && !isError && currentData.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between p-6 border-t border-gray-200 gap-4">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{endIndex}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </div>

              <div className="flex items-center gap-4">
                {/* Rows per page */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show:</span>
                  <div className="relative">
                    <select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="appearance-none border border-gray-300 rounded-md pl-3 pr-8 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoading}
                    >
                      {[5, 10, 15, 25, 50, 100].map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                    >
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>

                {/* Pagination controls */}
                <div className="flex items-center gap-1">
                  {/* First page */}
                  <button
                    onClick={goToFirstPage}
                    disabled={currentPage === 1 || isLoading}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Previous page */}
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage === 1 || isLoading}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Page number */}
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>

                  {/* Next page */}
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages || isLoading}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Last page */}
                  <button
                    onClick={goToLastPage}
                    disabled={currentPage === totalPages || isLoading}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M3.293 15.707a1 1 0 010-1.414L7.586 10 3.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Category Modal */}
      <NewCategoryModal
        isOpen={showNewCategoryModal}
        onClose={() => setShowNewCategoryModal(false)}
        onCategoryAdded={handleCategoryAdded}
      />

      {/* Confirm Delete Modal */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          if (deletingCategoryId) {
            deleteCategoryMutation.mutate(deletingCategoryId);
          }
        }}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone."
        confirmText={deleteCategoryMutation?.isPending ? 'Deleting...' : 'Delete'}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
      />

      {/* Edit Category Modal */}
      <EditCategoryModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        category={selectedCategory}
      />
    </>
  );
};

export default ManageCategoryModal;